// authSlice.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "./store";
import { CharitiesAUTH } from "@/models/charity";
import { ProjectsAUTH } from "@/models/project";
import { DonorOrgAUTH } from "@/models/donorOrg";
import Cookies from "universal-cookie";

// Define a type for the slice state
interface AuthState {
  isLoggedIn: boolean;
  token: string | null;
  firstname: string | null;
  lastname: string | null;
  email: string | null;
  bio: string | null;
  introduction: string | null;
  charities: CharitiesAUTH[] | null;
  donorOrgs?: DonorOrgAUTH[] | null;
  projects: ProjectsAUTH[] | null;
  userProjectMemberships?: ProjectsAUTH[] | null;
  userId: number | null;
  avatarid: string | null;
  isAdmin?: boolean;
  userType?: string;
  charity: CharitiesAUTH | null;
  roles: string[];
  isOffline?: boolean;
  walletAddress?: string | null;
  marketingEmailConsent?: boolean;
  impactUpdateConsent?: boolean;
  preferredCurrency?: string | null; // ✅ NEW
}

// Define the initial state using that type
const initialState: AuthState = {
  isLoggedIn: false,
  token: null,
  firstname: null,
  lastname: null,
  email: null,
  bio: null,
  introduction: null,
  charities: null,
  donorOrgs: null,
  projects: null,
  userProjectMemberships: null,
  userId: null,
  avatarid: null,
  isAdmin: false,
  charity: null,
  roles: [],
  isOffline: false,
  walletAddress: null,
  marketingEmailConsent: false,
  impactUpdateConsent: true,
  preferredCurrency: null,
};

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    // Action to set the token manually
    setToken(state, action: PayloadAction<string | null>) {
      state.token = action.payload;
      state.isLoggedIn = action.payload !== null;
    },

    setAvatarid: (
      state,
      action: PayloadAction<{
        isAdmin: boolean;
        token: string | null;
        firstname: string | null;
        lastname: string | null;
        email: string | null;
        userType: string | null;
        bio: string | null;
        introduction: string | null;
        charities?: CharitiesAUTH[] | null;
        donorOrgs?: DonorOrgAUTH[] | null;
        projects?: ProjectsAUTH[] | null;
        userProjectMemberships?: ProjectsAUTH[] | null;
        userId: number | null;
        avatarid: string | null;
        roles: string[] | null;
        walletAddress?: string | null;
        marketingEmailConsent?: boolean;
        impactUpdateConsent?: boolean;
        preferredCurrency?: string;
      }>,
    ) => {
      const { avatarid } = action.payload;
      state.avatarid = avatarid;
    },

    // Action to logout
    logout(state) {
      state.isLoggedIn = false;
      state.token = null;
      state.firstname = null;
      state.lastname = null;
      state.email = null;
      state.charities = [];
      state.donorOrgs = [];
      state.projects = [];
      state.userProjectMemberships = [];
      state.userId = null;
      state.avatarid = null;
      state.bio = null;
      state.introduction = null;
      state.roles = [];
      state.walletAddress = null;
      state.marketingEmailConsent = false;
      state.impactUpdateConsent = true;
      state.preferredCurrency = null;

      delete state.userType;

      const cookies = new Cookies();
      cookies.remove("jwt_authorisation", {
        path: "/",
        domain: ".philanthrify.co.uk",
      });

      if (typeof window !== "undefined") {
        localStorage.removeItem("persist:auth");
        localStorage.removeItem("persist:root");
        localStorage.removeItem("userSelectedProfile");
      }
    },

    setOfflineStatus(state, action: PayloadAction<boolean>) {
      state.isOffline = action.payload;
    },

    // Action to login
    login: (
      state,
      action: PayloadAction<{
        token: string | null;
        firstname: string | null;
        lastname: string | null;
        email: string | null;
        bio?: string | null;
        introduction?: string | null;
        charities?: CharitiesAUTH[] | null;
        donorOrgs?: DonorOrgAUTH[] | null;
        projects?: ProjectsAUTH[] | null;
        userProjectMemberships?: ProjectsAUTH[] | null;
        userId: number | null;
        avatarid?: string | null;
        userType?: string | null;
        walletAddress?: string | null;
        roles: string[];
        marketingEmailConsent: boolean;
        impactUpdateConsent?: boolean;
        preferredCurrency?: string;
      }>,
    ) => {
      if (state.isOffline) {
        console.warn("Cannot login while offline");
        return;
      }

      const {
        token,
        firstname,
        lastname,
        email,
        bio,
        introduction,
        charities,
        projects,
        userProjectMemberships,
        donorOrgs,
        userId,
        avatarid,
        userType,
        walletAddress,
        roles,
        marketingEmailConsent,
        impactUpdateConsent,
        preferredCurrency,
      } = action.payload;
      state.isLoggedIn = true;
      state.token = token;
      state.firstname = firstname;
      state.lastname = lastname;
      state.email = email;
      state.userId = userId;
      state.roles = roles;
      state.marketingEmailConsent = marketingEmailConsent;
      state.impactUpdateConsent =
        impactUpdateConsent !== undefined ? impactUpdateConsent : true;
      state.preferredCurrency = preferredCurrency;
      if (bio) state.bio = bio;
      if (introduction) state.introduction = introduction;
      if (avatarid) state.avatarid = avatarid;
      if (charities) state.charities = charities;
      if (donorOrgs) state.donorOrgs = donorOrgs;
      if (projects) state.projects = projects;
      if (userProjectMemberships)
        state.userProjectMemberships = userProjectMemberships;
      if (userType) state.userType = userType;
      if (walletAddress) state.walletAddress = walletAddress;
    },

    // ✅ NEW: Universal update action for partial state updates
    updateAuthState: (state, action: PayloadAction<Partial<AuthState>>) => {
      Object.keys(action.payload).forEach((key) => {
        const k = key as keyof AuthState;
        if (action.payload[k] !== undefined) {
          (state as any)[k] = action.payload[k];
        }
      });
    },

    // ✅ FIX: Update both 'projects' AND 'userProjectMemberships'
    addProject: (state, action: PayloadAction<ProjectsAUTH>) => {
      // 1. Add to the general projects list
      if (state.projects) {
        state.projects.push(action.payload);
      } else {
        state.projects = [action.payload];
      }

      // 2. ADD THIS BLOCK 👇
      // This ensures the project appears in your transaction dropdowns immediately
      if (state.userProjectMemberships) {
        state.userProjectMemberships.push(action.payload);
      } else {
        state.userProjectMemberships = [action.payload];
      }
    },

    // ✅ NEW: Update an existing project
    updateProject: (
      state,
      action: PayloadAction<{ id: number; updates: Partial<ProjectsAUTH> }>,
    ) => {
      if (state.projects) {
        const index = state.projects.findIndex(
          (p) => p.id === String(action.payload.id),
        );
        if (index !== -1) {
          state.projects[index] = {
            ...state.projects[index],
            ...action.payload.updates,
          };
        }
      }
    },

    // ✅ NEW: Remove a project (Fixed for Type Mismatch)
    removeProject: (state, action: PayloadAction<number | string>) => {
      // 1. Convert the ID to delete to a string
      const idToRemove = String(action.payload);

      // 2. Remove from general projects list
      if (state.projects) {
        state.projects = state.projects.filter(
          (p) => String(p.id) !== idToRemove, // 👈 CRITICAL CHANGE HERE
        );
      }

      // 3. Remove from userProjectMemberships (The dropdown list)
      if (state.userProjectMemberships) {
        state.userProjectMemberships = state.userProjectMemberships.filter(
          (p) => String(p.id) !== idToRemove, // 👈 CRITICAL CHANGE HERE
        );
      }
    },

    // ✅ NEW: Update user roles
    updateRoles: (state, action: PayloadAction<string[]>) => {
      state.roles = action.payload;
    },

    // ✅ NEW: Add a role
    addRole: (state, action: PayloadAction<string>) => {
      if (!state.roles.includes(action.payload)) {
        state.roles.push(action.payload);
      }
    },

    // ✅ NEW: Remove a role
    removeRole: (state, action: PayloadAction<string>) => {
      state.roles = state.roles.filter((role) => role !== action.payload);
    },

    // ✅ NEW: Add a role to a specific Donor Org
    // Payload: { id: "donorOrgId", role: "Admin" }
    addDonorOrgRole: (
      state,
      action: PayloadAction<{ id: string | number; role: string }>,
    ) => {
      const { id, role } = action.payload;
      const orgIdStr = String(id);

      // 1. Update the specific Donor Org
      if (state.donorOrgs) {
        const orgIndex = state.donorOrgs.findIndex(
          (d) => String(d.id || d.donorOrgId) === orgIdStr,
        );

        if (orgIndex !== -1) {
          const org = state.donorOrgs[orgIndex];
          // Ensure roles array exists
          if (!org.roles) org.roles = [];

          // Add role if not present
          if (!org.roles.includes(role)) {
            org.roles.push(role);
          }
        }
      }

      // 2. Also update global roles list if it's new there too
      if (!state.roles.includes(role)) {
        state.roles.push(role);
      }
    },

    // ✅ NEW: Remove a role from a specific Donor Org
    removeDonorOrgRole: (
      state,
      action: PayloadAction<{ id: string | number; role: string }>,
    ) => {
      const { id, role } = action.payload;
      const orgIdStr = String(id);

      // 1. Remove from specific Donor Org
      if (state.donorOrgs) {
        const orgIndex = state.donorOrgs.findIndex(
          (d) => String(d.id || d.donorOrgId) === orgIdStr,
        );

        if (orgIndex !== -1 && state.donorOrgs[orgIndex].roles) {
          state.donorOrgs[orgIndex].roles = state.donorOrgs[
            orgIndex
          ].roles.filter((r) => r !== role);
        }
      }
    },

    // ✅ NEW: Update charities
    updateCharities: (state, action: PayloadAction<CharitiesAUTH[]>) => {
      state.charities = action.payload;
    },

    // ✅ NEW: Update donor organizations
    updateDonorOrgs: (state, action: PayloadAction<DonorOrgAUTH[]>) => {
      state.donorOrgs = action.payload;
    },

    // ✅ NEW: Update user project memberships
    updateUserProjectMemberships: (
      state,
      action: PayloadAction<ProjectsAUTH[]>,
    ) => {
      state.userProjectMemberships = action.payload;
    },

    // ✅ NEW: Refresh all user data from server
    refreshUserData: (
      state,
      action: PayloadAction<{
        firstname?: string | null;
        lastname?: string | null;
        email?: string | null;
        bio?: string | null;
        introduction?: string | null;
        charities?: CharitiesAUTH[] | null;
        donorOrgs?: DonorOrgAUTH[] | null;
        projects?: ProjectsAUTH[] | null;
        userProjectMemberships?: ProjectsAUTH[] | null;
        avatarid?: string | null;
        userType?: string | null;
        walletAddress?: string | null;
        roles?: string[];
        marketingEmailConsent?: boolean;
        impactUpdateConsent?: boolean;
        preferredCurrency?: string;
      }>,
    ) => {
      const {
        firstname,
        lastname,
        email,
        bio,
        introduction,
        charities,
        projects,
        userProjectMemberships,
        donorOrgs,
        avatarid,
        userType,
        walletAddress,
        roles,
        marketingEmailConsent,
        impactUpdateConsent,
        preferredCurrency,
      } = action.payload;

      // Only update fields that are provided
      if (firstname !== undefined) state.firstname = firstname;
      if (lastname !== undefined) state.lastname = lastname;
      if (email !== undefined) state.email = email;
      if (bio !== undefined) state.bio = bio;
      if (introduction !== undefined) state.introduction = introduction;
      if (avatarid !== undefined) state.avatarid = avatarid;
      if (charities !== undefined) state.charities = charities;
      if (donorOrgs !== undefined) state.donorOrgs = donorOrgs;
      if (projects !== undefined) state.projects = projects;
      if (userProjectMemberships !== undefined)
        state.userProjectMemberships = userProjectMemberships;
      if (userType) state.userType = userType;
      if (walletAddress !== undefined) state.walletAddress = walletAddress;
      if (roles !== undefined) state.roles = roles;
      if (marketingEmailConsent !== undefined)
        state.marketingEmailConsent = marketingEmailConsent;
      if (impactUpdateConsent !== undefined)
        state.impactUpdateConsent = impactUpdateConsent;
      if (preferredCurrency !== undefined)
        state.preferredCurrency = preferredCurrency;
    },
  },
});

// Export actions
export const {
  setToken,
  logout,
  login,
  setAvatarid,
  addProject,
  updateProject,
  removeProject,
  setOfflineStatus,
  updateAuthState,
  updateRoles,
  addRole,
  removeRole,
  updateCharities,
  updateDonorOrgs,
  updateUserProjectMemberships,
  refreshUserData,
  addDonorOrgRole,
  removeDonorOrgRole,
} = authSlice.actions;

// Selectors
export const selectToken = (state: RootState) => state.auth.token;
export const selectUserId = (state: RootState) => state.auth.userId;
export const selectUserEmail = (state: RootState) => state.auth.email;
export const selectUserBio = (state: RootState) => state.auth.bio;
export const selectUserType = (state: RootState) => state.auth.userType;
export const selectIsOffline = (state: RootState) => state.auth.isOffline;
export const selectProjects = (state: RootState) => state.auth.projects;
export const selectRoles = (state: RootState) => state.auth.roles;
export const selectCharities = (state: RootState) => state.auth.charities;

// Export reducer
export default authSlice.reducer;
